export type Point = [number, number];
export type Bezier = [number, number, number, number];

/**
 * Convert percentage coordinates to pixel coordinates
 *
 * @param width - Width of the container in pixels
 * @param height - Height of the container in pixels
 * @param coords - Coordinates as percentages [x%, y%]
 * @returns Pixel coordinates [x, y]
 */
const convertToPxs = (width: number, height: number, coords: Point): Point => {
  const toPx = (dimension: number, percent: number): number => Math.round(dimension * (percent / 100));

  return [toPx(width, coords[0]), toPx(height, coords[1])];
};

/**
 * Apply absolute positions to relative bezier curve coordinates
 *
 * @param start - Start point coordinates [x, y]
 * @param end - End point coordinates [x, y]
 * @param absoluteCurve - Absolute bezier curve coordinates [x1, y1, x2, y2]
 * @returns Adjusted bezier curve coordinates [x1, y1, x2, y2]
 */
const applyPositions = (start: Point, end: Point, absoluteCurve: Bezier): Bezier => {
  const addPos = (pointCoord: Point, bezierCoord: Point): Point =>
    pointCoord.map((pos, i) => pos + bezierCoord[i]) as Point;

  return [
    ...addPos(start, absoluteCurve.slice(0, 2) as Point),
    ...addPos(end, absoluteCurve.slice(2) as Point)
  ] as Bezier;
};

/**
 * Push bezier control points away from mouse position with distance-based scaling
 *
 * @param beziers - Current bezier curve coordinates [x1, y1, x2, y2]
 * @param mousePos - Mouse position [x, y]
 * @param softAnimation - Whether to use softer animation parameters
 * @returns Adjusted bezier curve coordinates [x1, y1, x2, y2]
 */
const pushBeziers = (beziers: Bezier, mousePos: Point, softAnimation?: boolean): Bezier => {
  const mapRange = (n: number): number => {
    // Map 0-radius+ to max-1
    let radius = 400;
    let max = 1.6;
    if (softAnimation) {
      radius = 200;
      max = 1.4;
    }

    n = Math.min(n, radius);
    const ratio = (1 - max) / (radius - 0);
    const y = (n - 0) * ratio + max;
    return y;
  };

  const calcDistance = ([x, y]: Point): number => Math.sqrt(x ** 2 + y ** 2);

  const diffs = beziers.map((pos, i) => pos - mousePos[i % mousePos.length]);

  const distances: [number, number] = [calcDistance(diffs.slice(0, 2) as Point), calcDistance(diffs.slice(2) as Point)];

  const scalars = distances.map(distance => mapRange(distance));

  const scaledBeziers = diffs.map((diff, i) => {
    const distanceIndex = i <= 1 ? 0 : 1;
    return diff * scalars[distanceIndex] + mousePos[i % mousePos.length];
  });

  return scaledBeziers as Bezier;
};

export { convertToPxs, applyPositions, pushBeziers };
